import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ProductType } from '@yme/common/constants';
import { getAuthorizedStatus } from '@yme/components/Header/ProfileMenu/redux/selectors';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { getIsKioskVersion } from '@yme/selectors/common';
import { API } from './api';
import { getSkus, temporaryItem } from './utils';
export function useWishlist() {
    const dispatch = useDispatch();
    const isAuthorized = useSelector(getAuthorizedStatus);
    const isKioskVersion = useSelector(getIsKioskVersion);
    const [items, setItems] = useState(API.normalize());
    const [localState] = useState(new WeakSet());
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    const updateItems = useRef((_, force = false) => { });
    const currentItemsRef = useRef(items);
    const wishlist = useRef(new Set([]));
    wishlist.current = new Set(items.map((el) => el.sku));
    updateItems.current = (newItems, force = false) => {
        if (force) {
            setItems(newItems);
            return;
        }
        // list of locally created items
        const localItems = items.filter((el) => localState.has(el));
        // we need to filter out locally created items if they are in newItems
        const nonPersistedLocalItems = localItems.filter((el) => !newItems.some((newEl) => newEl.sku === el.sku));
        setItems([...newItems, ...nonPersistedLocalItems]);
    };
    currentItemsRef.current = items;
    useEffect(() => {
        API.onChange.push(updateItems);
        return () => {
            API.onChange = API.onChange.filter((el) => el !== updateItems);
        };
    }, [updateItems]);
    useEffect(() => {
        if (!isAuthorized) {
            API.clear();
            return;
        }
        if (API.items.length) {
            return;
        }
        API.load();
    }, [isAuthorized]);
    const isCommitting = (item) => {
        return getSkus(item).some((sku) => API.pendingItems.has(sku));
    };
    const isLiked = (item) => {
        if (!item) {
            return false;
        }
        return getSkus(item).some((sku) => wishlist.current.has(sku));
    };
    const removeFromWishlist = async (item) => {
        const skus = getSkus(item);
        const skusToRemove = skus.filter((sku) => wishlist.current.has(sku));
        for (const sku of skusToRemove) {
            const removedItem = currentItemsRef.current.find((el) => el.sku === sku);
            if (!removedItem) {
                continue;
            }
            try {
                updateItems.current(currentItemsRef.current.filter((el) => el !== removedItem));
                await API.remove(sku);
            }
            catch (e) {
                updateItems.current([...currentItemsRef.current, removedItem]);
            }
        }
    };
    const addToWishlist = async (item, pathName) => {
        const sku = typeof item === 'string' ? item : item.variants.find((v) => v.master)?.sku ?? '';
        if (isCommitting(sku)) {
            return;
        }
        const tempItem = temporaryItem(sku, pathName);
        localState.add(tempItem);
        updateItems.current([...currentItemsRef.current, tempItem]);
        try {
            await API.add(sku, pathName);
        }
        catch (e) {
            const updatedItems = currentItemsRef.current.filter((el) => el.sku !== sku);
            updateItems.current(updatedItems, true);
        }
    };
    const toggleWishlist = async (item, pathName) => {
        if (isCommitting(item)) {
            return;
        }
        if (isLiked(item)) {
            await removeFromWishlist(item);
        }
        else {
            await addToWishlist(item, pathName);
        }
    };
    const isWishlistEnabled = !isKioskVersion;
    return {
        items,
        isWishlistEnabled,
        isLiked,
        isAuthorized,
        isCommitting,
        isBusy(sku) {
            return API.pendingItems.has(sku);
        },
        onLike(sku, cardUrl, type) {
            if (type && type === ProductType.UNIT) {
                return undefined;
            }
            if (isWishlistEnabled && sku && cardUrl) {
                return (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isAuthorized) {
                        toggleWishlist(sku, cardUrl);
                    }
                    else {
                        API.addDelayedItem(sku, cardUrl);
                        dispatch(setIsModalShowed({
                            type: MODALS.LOGIN_REQUIRED_MODAL,
                        }));
                    }
                    return sku;
                };
            }
            else {
                return undefined;
            }
        },
        toggleWishlist,
        addToWishlist,
        removeFromWishlist,
    };
}
